/**
 * Reports when the renderer's legacy lifecycle and the Remix state machine settle on different
 * phases. This is a read-only observer: it derives nothing the renderer does not already compute for
 * itself, and it never participates in renderer behavior.
 *
 * Classification lives in `rendererRemixParity.ts`; this hook owns only settle timing and per-flow
 * deduplication.
 */
import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	createDivergenceSignature,
	dispatchRendererRemixParityEvent,
} from './dispatchRendererRemixParityEvent.ts';
import type { RendererRemixLifecyclePhase } from './rendererRemixLifecycle.ts';
import { compareRendererRemixParity, isTerminalPhase } from './rendererRemixParity.ts';

/**
 * How long a non-terminal phase pair must hold before it counts as settled.
 *
 * Legacy readiness and the machine's watcher-driven phase land on different ticks, so a healthy
 * flow briefly reports two different phases at every transition. Reporting only what survives this
 * delay keeps that ordinary skew out of the signal.
 *
 * This delay is deliberately not applied once the machine is terminal — see the comment at the
 * report site.
 */
const PARITY_SETTLE_MS = 250;

type ReportedDivergences = Readonly<{
	remixId: string;
	signatures: Set<string>;
}>;

export const useRendererRemixParityDiagnostics = ({
	enabled,
	remixId,
	legacyPhase,
	machinePhase,
}: Readonly<{
	enabled: boolean;
	remixId: string | undefined;
	legacyPhase: RendererRemixLifecyclePhase;
	machinePhase: RendererRemixLifecyclePhase;
}>): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportedRef = useRef<ReportedDivergences>();

	useEffect(() => {
		if (!enabled || remixId === undefined) {
			return;
		}

		// Waiting can only resolve `awaiting-stable-phases`. A match, or a pending result the machine
		// itself caused, is already final for this pair.
		const unsettled = compareRendererRemixParity({ legacyPhase, machinePhase, isStable: false });
		if (unsettled.outcome !== 'pending' || unsettled.reason !== 'awaiting-stable-phases') {
			return;
		}

		const reportSettledPair = (): void => {
			const settled = compareRendererRemixParity({ legacyPhase, machinePhase, isStable: true });
			if (settled.outcome !== 'divergence') {
				return;
			}

			// Each admitted flow reports a given divergence once, so a divergence that persists across
			// many renders does not become a flood of identical events.
			const previous = reportedRef.current;
			const reported: ReportedDivergences =
				previous?.remixId === remixId ? previous : { remixId, signatures: new Set<string>() };
			reportedRef.current = reported;

			const signature = createDivergenceSignature(settled.divergence);
			if (reported.signatures.has(signature)) {
				return;
			}
			reported.signatures.add(signature);

			dispatchRendererRemixParityEvent(createAnalyticsEvent, settled.divergence);
		};

		// A terminal machine phase is already final, and deferring it would lose the observation: in
		// treatment the renderer's error and abort effects are driven by the machine phase and call
		// `onClose`, which clears the modal session and unmounts this observer in the same cycle,
		// clearing any pending timer. Report now or never.
		//
		// The mirror case is deliberately not fast-pathed. During an ordinary error or abort the
		// legacy phase updates during render while the machine catches up in a later effect, so a
		// terminal legacy phase against a live machine is expected lag rather than a divergence. Those
		// pairs keep the settle window so the machine can converge.
		if (isTerminalPhase(machinePhase)) {
			reportSettledPair();
			return;
		}

		const timeout = setTimeout(reportSettledPair, PARITY_SETTLE_MS);

		return () => clearTimeout(timeout);
	}, [createAnalyticsEvent, enabled, legacyPhase, machinePhase, remixId]);
};
